/**
 * ================================================================================================
 *  ONE MARKET READING, AS A SIDE-COLUMN CARD. **§4, 2026-10-29.**
 * ================================================================================================
 *
 * The Owner: *"נתוני השוק — מט״ח, ריבית, מדד ודלק — יהיו באזור הצד השמאלי, מתחת ל„שימוש
 * לאחרונה”"*, and his rule about every figure the product shows:
 *
 *     *"„אין נתונים — לעולם לא אפס” רחב מדי: ספירה אמיתית שהחזירה אפס צריכה להציג 0. כשל טעינה
 *     או היעדר חיבור לא יוצגו כאפס."*
 *
 * ── FOUR STATES, AND THE CARD DOES NOT DECIDE WHICH ONE IT IS IN ────────────────────────────────
 *
 * The server answers `state` per domain and this component renders it. That is not deference for
 * its own sake: whether a reading is STALE depends on the source's own `refresh_minutes`, which
 * lives in a row the browser never sees, and a card that guessed from a timestamp would be a second
 * implementation of a rule it holds none of the inputs to.
 *
 *   `not_configured`  nobody has chosen a publisher. **Not a failure** — it says so, and names no
 *                     publisher, because there is nobody to name.
 *   `unavailable`     a source exists and has produced no reading. **Never a zero.**
 *   `stale`           a reading older than its source's refresh window. **SHOWN, with its own
 *                     date**, because a rate from this morning is useful and an empty card is not.
 *   `fresh`           shown.
 *
 * ── THE DATE IS THE PUBLISHER'S AND NEVER OURS ──────────────────────────────────────────────────
 *
 * `observedAtUtc` is when the SOURCE says the value was true. `lastSuccessAtUtc` is when this
 * product last asked, and it appears only in the staleness sentence — never as the reading's date.
 * A card that printed the fetch time as *"נכון ל…"* would tell somebody a Friday rate was current
 * on Sunday morning because the product had just asked.
 *
 * ── AND THE UNIT COMES FROM THE SERVER TOO ──────────────────────────────────────────────────────
 *
 * The Bank of Israel's interest answer names no unit at all; `3.25` is a percentage because of what
 * the figure IS. So the unit is declared on the source row and rendered from there, and this card
 * never infers one from the shape of a number.
 */

import type { JSX } from 'react';

import { WidgetNone, WidgetPanel, WidgetReadFault, WidgetReading } from './WidgetPanel';
import { ServerRead } from './ServerRead';
import { useLocale } from '../i18n/LocaleProvider';
import type { IconName } from '../shell/Icon';
import type { ServerData } from '../api/useServerData';
import type { MarketCardView } from '../api/contract';
import type { TextKey } from '../i18n/catalogue';
import { chosenCurrencies, DEFAULT_CURRENCIES } from '../shell/currencies';

/** How many series one side-column card shows. The column is narrow; the feed does not decide. */
const SERIES_SHOWN = 4;

/**
 * ── A SERIES CODE IS NOT A NAME, EXCEPT WHEN IT IS. **Owner, section 5, 2026-11-01.** ───────────
 *
 *     *"תקן הצמדת קודי מטבע למספרים, ערבוב כיווניות, `policy_rate` ותאריך המוצג במקום ריבית."*
 *
 * The row rendered `seriesLabelHe ?? seriesCode`, so a source row with no Hebrew label put its
 * INTERNAL CODE on the Owner's home screen — `policy_rate`, in so many words. A fallback to the
 * code is the right instinct for `USD`, which IS what a person calls that series, and the wrong
 * one for everything else.
 *
 * So the fallback is narrowed to codes that are already display tokens: two to six characters,
 * upper-case Latin letters and digits only. `USD`, `EUR`, `GBP`, `BRENT`, `95` pass. `policy_rate`,
 * `cpi_general`, `fuel_95_self` do not, and a series whose code is not a display token takes the
 * CARD's own name — which is correct, because a card with one unnamed series is a card about that
 * one reading.
 *
 * THE RULE IS ON THE SHAPE AND NOT ON A LIST OF KNOWN CODES. A list would be a second copy of the
 * source table, maintained here, and the day a new series arrived it would show its code again.
 */
const DISPLAY_TOKEN = /^[A-Z0-9]{2,6}$/u;

/**
 * ================================================================================================
 *  THE CURRENCIES, BY NAME. **Owner, section 2, 2026-11-01.**
 * ================================================================================================
 *
 *     *"בווידג׳ט שערי המטבע יוצגו דולר ואירו כברירת מחדל, עם שם המטבע והקוד שלו, השער בשקלים
 *     ובסיס יחידות ברור."*
 *
 * ── WHY THE NAME IS HERE AND NOT ON THE WIRE ───────────────────────────────────────────────────
 *
 * The Bank of Israel's answer carries `key`, `currentExchangeRate`, `currentChange`, `unit` and
 * `lastUpdate`. **There is no name in it**, in any language — so `series_label_he` is `null` for
 * every currency row, and the card showed the bare code. A Hebrew currency name is a TRANSLATION,
 * which is this file's side of the product, and inventing one on the server would put Hebrew
 * prose in a collector.
 *
 * ── IT IS A CLOSED LIST, AND AN UNKNOWN CODE IS NOT A DEFECT ───────────────────────────────────
 *
 * Four names, for the four currencies the source has ever returned to this product. A code with
 * no entry renders exactly as it did before — the code alone, through `seriesName` — which is the
 * honest fallback: the alternative is a map pretending to be exhaustive, and a card that prints
 * nothing on the day the publisher adds the Swiss franc.
 */
/**
 * ── THE RATE CARD'S ROWS, BY NAME. **Owner, 2026-11-01.** ─────────────────────────────────────
 *
 *     *"ריביות והצמדה צריך להיות ריבית ופריים, ושלחתי לך תמונה איך זה צריך להיות."*
 *
 * His card has three named rows: the Bank of Israel's rate, the prime, and when the next
 * decision falls. The publisher names none of them — the answer carries `key` and a number — so
 * the Hebrew is a translation and lives here, exactly as the currency names do.
 *
 * `next_decision` IS A DATE AND NOT A RATE, and it has been in the payload since 0029 while
 * nothing rendered it: the card keeps only series with a decimal, which is the rule that stopped
 * a date being printed WHERE THE RATE BELONGS. A named row cannot make that mistake — the name
 * is what distinguishes `החלטה הבאה · 23/11/26` from a rate — so a series with a name of its own
 * may render its text.
 */
const SERIES_NAME: Readonly<Record<string, TextKey>> = {
  policy_rate: 'market.series.policyRate',
  prime_rate: 'market.series.primeRate',
  next_decision: 'market.series.nextDecision',
  /*
   * ── AND THE INDEX. **Owner, 2026-11-01.** ────────────────────────────────────
   *
   *     *"למה הגופן של המדד לא בגודל כמו ריבית ופריים ושערי מטבע?"*
   *
   * BECAUSE IT HAD NO NAME, and that is the whole mechanism rather than a styling accident: a
   * reading with no name of its own IS the card's figure and is set larger to say so
   * (`data-solo`). The rates card stopped being one when its rows were named; the index was the
   * last card in the rail with a single unnamed reading.
   *
   * Naming it is the fix and not an exception to the sizing rule — and it is the name his own
   * reference card uses for that row.
   */
  /*
   * `general` IS THE CODE THE COLLECTOR WRITES, and `cpi_general` was never one of them.
   * `parseCbsPriceIndex` has emitted `general` since 0029; the harness fixture used
   * `cpi_general`, I mapped the fixture's spelling, and the mapping would have matched NOTHING
   * on the estate — the row would have fallen back to the card's own heading and rendered as the
   * card's figure, which is the bug the Owner reported about the font size, one layer down.
   * Both are listed: the live code, and the fixture's, until that fixture is corrected.
   */
  general: 'market.series.indexValue',
  cpi_general: 'market.series.indexValue',
  /*
   * THE FUEL ROWS. The ministry's workbook has two columns under price control and the parser
   * emits one series each; the Hebrew is here for the same reason every other name is — the
   * publisher names nothing, and `petrol_95` is a code.
   */
  petrol_95: 'market.series.petrol95',
  petrol_95_full: 'market.series.petrol95Full',
  /* The index card's four rows, in the Owner's own words from the table he sent. */
  cpi_month: 'market.series.indexMonth',
  cpi_change_monthly: 'market.series.indexMonthly',
  cpi_change_yearly: 'market.series.indexYearly',
};

const CURRENCY_NAME: Readonly<Record<string, TextKey>> = {
  USD: 'market.currency.usd',
  EUR: 'market.currency.eur',
  GBP: 'market.currency.gbp',
  JPY: 'market.currency.jpy',
};

/**
 * ── WHICH CURRENCIES, AND IT IS HIS CHOICE AND NOT A CONSTANT. **Owner, 2026-11-01.** ─────────
 *
 * Three readings of one sentence in one day, and the third is his:
 *
 *   1. *"יוצגו דולר ואירו כברירת מחדל"* → I read it as ORDER and showed all four, dollar first.
 *   2. *"אני לא צריך לירה ויין, מספיק לי דולר ואירו"* → a constant of two, in this file.
 *   3. *"אני רוצה לבחור איזה שער להוסיף. אני רוצה להוסיף את ה־4 מדדים שהיו. כאשר סידרת את
 *      הגופן אין בעיה להוסיף עוד."*
 *
 * The first two were me deciding in code what is a CHOICE — and the second one was only true
 * while the rows were too tall to fit four. `shell/currencies.ts` holds it as a preference now,
 * per user and across devices, defaulting to the four the source has always sent; the picker is
 * on the personalisation screen with the other display choices.
 *
 * THE ORDER IS THE DEFAULT'S. A person choosing a set is not choosing an arrangement, and a card
 * whose rows moved with the order somebody happened to tick them in cannot be scanned twice.
 */
function currencyOrder(code: string, chosen: readonly string[]): number {
  const at = DEFAULT_CURRENCIES.indexOf(code);
  return at === -1 ? DEFAULT_CURRENCIES.length + chosen.indexOf(code) : at;
}

function seriesName(
  entry: { readonly seriesCode: string; readonly seriesLabelHe: string | null },
  cardNameHe: string,
): string {
  if (entry.seriesLabelHe !== null && entry.seriesLabelHe !== '') return entry.seriesLabelHe;
  return DISPLAY_TOKEN.test(entry.seriesCode) ? entry.seriesCode : cardNameHe;
}

/**
 * ── A NUMERIC CARD SHOWS A NUMBER OR IT SHOWS NOTHING. **The other half of the same finding.** ──
 *
 * *"ותאריך המוצג במקום ריבית"* — a DATE where the interest rate belongs. The row read
 * `valueDecimal ?? valueText`, and `valueText` is the free-text column: on a source that carries
 * its effective date there, the card printed the date as the reading.
 *
 * `valueText` is for the one domain whose series ARE text — the professional-updates feed, whose
 * rows are a headline and a link. Everywhere else a missing decimal means the publisher gave this
 * product no number, and the honest rendering of that is the no-reading sentence, never whatever
 * else happened to be in the row.
 */
const TEXT_DOMAIN: MarketCardView['domainCode'] = 'professional_updates';

export interface MarketTileProps {
  readonly titleHe: string;
  readonly icon: IconName;
  /**
   * THE READ'S STATE AND NOT A MAYBE-CARD.
   *
   * The first draft took `MarketCardView | undefined` and rendered *"the component has not been
   * built"* whenever it was absent -- collapsing a read IN FLIGHT, a read that FAILED and a domain
   * the answer did not carry into one sentence, and the wrong one. `<ServerRead>` owns loading and
   * failure for every other card on this screen; this one does not re-decide them.
   */
  readonly result: ServerData<{ readonly cards: readonly MarketCardView[] }>;
  readonly domain: MarketCardView['domainCode'];
}

/**
 * THE UNIT THE SERVER DECLARED, AS A LABEL.
 *
 * `%` AND `₪` ARE LITERALS HERE AND NOT CATALOGUE KEYS, and a control is why:
 * `identity-language.test.ts` requires a Hebrew label to carry a Hebrew letter -- that is how it
 * catches a `he` field filled in with the English -- and it refused both symbols.
 *
 * Adding them to that test's exception list would have been the wrong answer. The list is for
 * product names and acronyms that are the same WORD in both languages; a unit symbol is not
 * language at all. The units that are PROSE stay in the catalogue, where a translator can reach
 * them.
 */
function unitLabel(
  unit: string | null,
  t: (key: 'market.unit.points' | 'market.unit.ilsPerLitre') => string,
): string | undefined {
  switch (unit) {
    case 'percent':
      return '%';
    case 'ils':
      return '₪';
    case 'index_points':
      return t('market.unit.points');
    case 'ils_per_litre':
      return t('market.unit.ilsPerLitre');
    default:
      return undefined;
  }
}

/**
 * ── A READING, WITHOUT THE ZEROES THE COLUMN ADDS. **Found in a browser, 2026-11-01.** ───────
 *
 * The fuel card rendered `7.350000` and `0.260000`. `core.market_reading.value_decimal` is
 * `DECIMAL(18,6)`, so a value comes back from the database with six places whatever the publisher
 * sent — and **this is not a fuel problem**: the rates card will read `4.250000` and the dollar
 * `3.641200` the moment they come from the estate rather than from a fixture whose strings happen
 * to be short. Three cards hid it and the fourth exposed it.
 *
 * TRIMMING IS NOT ROUNDING. Only zeros that change nothing go, and only after a decimal point:
 * `3.641200` → `3.6412`, `104.300000` → `104.3`, `8.000000` → `8`. A value with a significant
 * digit in the sixth place keeps all six, which is the case this must not quietly break.
 */
function trimZeros(value: string): string {
  if (!/^-?\d+\.\d+$/u.test(value)) return value;
  return value.replace(/0+$/u, '').replace(/\.$/u, '');
}

/**
 * ── AND A PRICE IN SHEKELS KEEPS ITS AGOROT. **Owner, 2026-11-01.** ──────────────────────────
 *
 *     *"אפשר בדלק להציג שתי ספרות אחרי הנקודה."*
 *
 * Trimming alone answers him today, because the ministry publishes `7.35` and `0.26` — and it
 * stops answering him the month a price lands on `8.00`, which trims to `8` and reads as a price
 * somebody rounded. A price per litre is MONEY, and money in this product shows agorot.
 *
 * IT IS KEYED ON THE UNIT AND NOT ON THE CARD, so the rule follows the kind of reading rather
 * than where it is drawn. A RATE IS NOT MONEY and keeps the publisher's own precision: the dollar
 * is `3.6412` because the Bank of Israel quotes four places, and forcing two would throw away
 * digits they sent.
 */
const FIXED_PLACES: Readonly<Record<string, number>> = {
  /* MONEY SHOWS AGOROT. *"אפשר בדלק להציג שתי ספרות אחרי הנקודה."* */
  ils_per_litre: 2,
  /*
   * A RATE KEEPS THE FOUR PLACES THE BANK OF ISRAEL QUOTES. Trimming alone rendered the pound as
   * `4.612` beside `3.6412` and `0.0241` -- a ragged column of numbers a person is comparing,
   * and a digit dropped from what the publisher actually said.
   */
  ils: 4,
};

function forDisplay(value: string, unit: string | null): string {
  const places = unit === null ? undefined : FIXED_PLACES[unit];
  if (places === undefined) return trimZeros(value);
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toFixed(places) : value;
}

/**
 * `2026-10-28T13:22:03.592Z` → `28/10/26`. The publisher's day, in the reader's order.
 *
 * ── `DD/MM/YY`, AND HE NAMED THE FORMAT. **Owner, 2026-11-01.** ──────────────────────────────
 *
 *     *"לא צריך את כל המלל הזה, מספיק נכון ל-30.10.26."*
 *     *"צריך להיות נכון ליום.. DD/MM/YY"*
 *
 * Slashes and two digits of year. The rail card is 190px wide and this line is the last thing
 * on it; four digits of century is the part of a date nobody reads on a reading published this
 * week. It is only the MARKET cards' format — every date that is a RECORD rather than a glance
 * keeps its century, because a four-digit year in an audit line is not clutter.
 */
function asDay(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const day = String(at.getUTCDate()).padStart(2, '0');
  const month = String(at.getUTCMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${String(at.getUTCFullYear()).slice(-2)}`;
}

export function MarketTile({ titleHe, icon, result, domain }: MarketTileProps): JSX.Element {
  /*
   * LOADING AND FAILURE ARE `<ServerRead>`'s, which is what keeps a failed read from ever becoming
   * a figure: a value exists only inside the branch where the read succeeded.
   */
  return (
    <ServerRead<{ readonly cards: readonly MarketCardView[] }>
      result={result}
      whatHe={titleHe}
      skeletonRows={1}
      /* A 404 on a LIST read is not a missing item -- see `WidgetReadFault`. */
      renderNotFound={() => <WidgetReadFault />}
    >
      {(data) => (
        <MarketBody
          titleHe={titleHe}
          icon={icon}
          /* `?? []`: a malformed answer renders the not-connected card, never a thrown render.
             See `UpdatesBanner` for the measurement -- both are in the shell now. */
          card={(data.cards ?? []).find((candidate) => candidate.domainCode === domain)}
        />
      )}
    </ServerRead>
  );
}

/** The part that renders a reading the read actually returned. */
function MarketBody({
  titleHe,
  icon,
  card,
}: {
  readonly titleHe: string;
  readonly icon: IconName;
  readonly card: MarketCardView | undefined;
}): JSX.Element {
  // `useLocale` HERE rather than `t` passed down: a translator function threaded through props is a
  // prop every caller can get wrong, and the hook is the house pattern.
  const { t } = useLocale();
  /*
   * HIS CHOSEN RATES, READ ON EVERY RENDER AND NOT HELD IN STATE. The list changes on the
   * personalisation screen, and in this product every link is a page load — so this component
   * is mounted fresh whenever it could have changed, and a subscription would be a mechanism
   * with no case to serve. `chosenCurrencies()` never answers an empty list; see its module.
   */
  const chosen = chosenCurrencies();
  /*
   * THE READ SUCCEEDED AND CARRIED NO SUCH DOMAIN. The server answers every domain, including the
   * ones with no source, so this is a disagreement between the two sides rather than an absence
   * about the world -- and `not_implemented` is the honest reading of it: this product answered
   * without the thing this card is for.
   */
  if (card === undefined) {
    return (
      <WidgetPanel
        titleHe={titleHe}
        icon={icon}
        absence={{ kind: 'not_implemented', reasonHe: t('market.noAnswer') }}
      />
    );
  }

  if (card.state === 'not_configured') {
    /*
     * `not_connected` AND NOT `not_implemented`. The Owner separated these two by name: this
     * capability EXISTS and no publisher has been chosen for it, which is a setting somebody can
     * make — unlike a tile with no component behind it, where there is no switch to find.
     *
     * ── AND IT CARRIES NO SENTENCE OF ITS OWN, WHICH THE COMPILER INSISTED ON. ───────────
     *
     * `WidgetAbsence` gives `reasonHe` to `not_implemented` and gives `not_connected` an optional
     * ACTION instead — *"where the office goes to set it up, given only when the office can
     * actually do it"*. That is a better shape than the one I reached for: a card that says
     * *"configuration required"* and offers no way to configure it is a dead end, and the generic
     * wording already says the right thing.
     *
     * NO ACTION IS GIVEN YET, deliberately: there is no screen for choosing a market publisher,
     * and a link to one that does not exist is the next step ending in a refusal that §8 forbids.
     * `market.notConfigured` stays in the catalogue for that screen's own copy.
     */
    return <WidgetPanel titleHe={titleHe} icon={icon} absence={{ kind: 'not_connected' }} />;
  }

  if (card.state === 'unavailable') {
    // A SOURCE WITH NO READING. Never a zero — the figure does not exist, and a nought would be a
    // measurement nobody made.
    return (
      <WidgetPanel titleHe={titleHe} icon={icon}>
        <WidgetNone
          bodyHe={
            card.lastAttemptFailed ? t('market.lastAttemptFailed') : t('market.noReadingYet')
          }
        />
      </WidgetPanel>
    );
  }

  const unit = unitLabel(card.valueUnit, t);
  /*
   * ORDERED BEFORE IT IS CAPPED, which is the whole point of ordering it: `slice` first would
   * take the publisher's own first four and then sort those, so a dollar in fifth place would
   * still be missing from a card that claims to show it by default.
   *
   * `[...]` because `card.series` is readonly and `sort` mutates. The comparison is on the
   * ORDER INDEX only, so every currency outside the preferred list compares equal and
   * `Array.prototype.sort` — stable since ES2019 — leaves them exactly as the source sent them.
   */
  const ordered =
    card.domainCode === 'fx'
      ? [...card.series]
          /* THE ONES HE CHOSE. Everything else the publisher sent is read, stored and not shown
             — *"אני רוצה לבחור איזה שער להוסיף"*. */
          .filter((entry) => chosen.includes(entry.seriesCode))
          .sort((a, b) => currencyOrder(a.seriesCode, chosen) - currencyOrder(b.seriesCode, chosen))
      : card.series;
  const series = ordered.slice(0, SERIES_SHOWN);
  /*
   * THE ROWS THIS CARD MAY ACTUALLY SHOW. A numeric domain keeps only the series that carry a
   * number; the text domain keeps the ones that carry text. Filtering here rather than in the
   * markup is what makes the empty case reachable: a card whose every series is unusable says
   * so once, instead of drawing four rows of nothing.
   */
  const usable =
    card.domainCode === TEXT_DOMAIN
      ? series.filter((entry) => (entry.valueText ?? '') !== '')
      : series.filter(
          (entry) =>
            entry.valueDecimal !== null ||
            /*
             * A NAMED READING IS ADMITTED WITHOUT A NUMBER, and only a named one. The rule this
             * relaxes was written for *"ותאריך המוצג במקום ריבית"* — a date printed where the
             * rate belongs — and what made that possible was the row having NO NAME of its own:
             * it became the card's figure. `החלטה הבאה · 23/11/26` and `שינוי שנתי · ▲ 1.60%`
             * are different objects, and the name is what makes them ones.
             */
            (SERIES_NAME[entry.seriesCode] !== undefined &&
              ((entry.valueText ?? '') !== '' || entry.changePercent !== null)),
        );
  const first = usable[0];

  return (
    <WidgetPanel titleHe={titleHe} icon={icon} domain={card.domainCode}>
      {first === undefined ? (
        <WidgetNone bodyHe={t('market.noReadingYet')} />
      ) : (
        <>
          {/*
            ── EVERY READING IS THE SAME ROW. **Owner, section 5, 2026-11-01.** ──────────────────

            *"סדר את התצוגה לשורות קריאות של שם, ערך ויחידה."* The headline used to be a large
            `<WidgetFigure>` and the rest a differently-shaped list, so one card had two layouts
            and the first series was told apart from the others by SIZE rather than by being
            named. Four identical rows are what a person scans; the card's own heading already
            says which reading this is.
          */}
          {card.domainCode === TEXT_DOMAIN
            ? usable.map((entry) => (
                <p className="gp-w-text" dir="auto" key={entry.seriesCode}>
                  {(entry.valueText ?? '').split('\t')[0]}
                </p>
              ))
            : usable.map((entry) => {
                /*
                  A SERIES THAT FELL BACK TO THE CARD'S OWN NAME GETS NO NAME ON ITS ROW. The
                  heading is directly above it, and *"ריביות והצמדה · ריביות והצמדה · 4.25"* is
                  the card saying its own name twice. The row then renders as the card's figure.
                */
                const name = seriesName(entry, titleHe);
                /*
                  THE NAME IS A TRANSLATION AND THE CODE IS THE PUBLISHER'S. `CURRENCY_NAME` wins
                  over the code fallback when it has an entry, and the code then renders beside
                  it -- *"שם המטבע והקוד שלו"*. A code with no entry keeps the old behaviour
                  exactly: the code IS the name, and there is no second copy of it on the row.
                */
                const named = CURRENCY_NAME[entry.seriesCode] ?? SERIES_NAME[entry.seriesCode];
                const nameHe = named === undefined ? name : t(named);
                /*
                  A READING WITH NO DECIMAL PRINTS ITS TEXT, which by the filter above can only be
                  a NAMED series — today that is `next_decision`, a date. It carries no unit: a
                  date measured in per cent is the defect this card was fixed for.
                */
                /*
                  WHAT GOES IN THE VALUE CELL. A decimal is formatted for its unit; a text
                  reading is its text — passed through `asDay` only when it IS a day, because
                  `09/2026` is a MONTH and feeding it to a date parser is how a period becomes
                  whatever the engine guesses. A row with neither is a change and nothing else,
                  and its cell is empty.
                */
                const text = entry.valueText ?? '';
                const isDay = /^\d{4}-\d{2}-\d{2}/u.test(text);
                return (
                  <WidgetReading
                    key={entry.seriesCode}
                    {...(nameHe === titleHe ? {} : { nameHe })}
                    {...(CURRENCY_NAME[entry.seriesCode] === undefined
                      ? {}
                      : { code: entry.seriesCode })}
                    value={
                      entry.valueDecimal !== null
                        ? forDisplay(entry.valueDecimal, card.valueUnit)
                        : isDay
                          ? asDay(text)
                          : text
                    }
                    {...(unit === undefined || entry.valueDecimal === null ? {} : { unitHe: unit })}
                    {...(entry.changePercent === null
                      ? {}
                      : { changePercent: entry.changePercent })}
                  />
                );
              })}

          {/*
            ── THE DATE AND THE UNITS. THE PUBLISHER'S NAME IS GONE. **Owner, 2026-11-01.** ──────

                *"אין צורך לרשום בנק ישראל..."*

            It was here under his earlier rule — *"הצג מקור, מועד עדכון ובסיס יחידות ברור"* — and
            he has now narrowed that rule for these cards. His reasoning is visible on the screen:
            three of the four rail cards name the same publisher, so the name is a line of chrome
            repeated down the column and never the thing anybody came to read.

            **WHAT IS KEPT IS WHAT CHANGES PER CARD**: whose date it is stays implicit, the DATE
            itself stays, and the unit basis stays. The publisher is still carried on the wire and
            is still what the collector records; this is the card not printing it.

            `lastSuccessAtUtc` is deliberately not here either: it belongs to the staleness
            sentence below and never to the reading's date.
          */}
          {/*
            ── WHAT THIS CARD DOES NOT CARRY, AND WHY. **Owner, 2026-11-01.** ──────────────────

            *"לא לשכוח להציג גם סולר."* Only 95-octane is under price control, so the ministry
            publishes no ceiling for diesel — see the string's own note. The line is on the fuel
            card alone: on any other card it would be answering a question nobody asked.
          */}
          {card.domainCode === 'fuel' ? (
            /*
              SMALL, ON HIS INSTRUCTION: *"את המשפט שהדלק אינו בפיקוח, אם זה נכון
              לרשום את זה בקטן בבקשה."* It is correct — the supervision order covers 95-octane
              alone — and it is a footnote rather than a reading, so it is set as one: the
              provenance size, not the body size.
            */
            <p className="gp-w-footnote">{t('market.fuelControlledOnly')}</p>
          ) : null}

          <p className="gp-muted gp-small gp-w-provenance">
            <span>
              {t('market.asOf')} {asDay(first.observedAtUtc)}
            </span>
            {/*
              ── AND THE UNIT BASIS LEFT THE LINE TOO. **Owner, 2026-11-01.** ──────────────────

                  *"לא צריך את כל המלל הזה, מספיק נכון ל-30.10.26."*

              It read `יחידות: ₪ ליחידת מטבע אחת`, which was added the same morning under his
              *"בסיס יחידות ברור"* — and he is right that it does not earn its line: **the
              symbol is already on every row**, beside the figure it belongs to, which is where a
              unit is actually read. What the sentence added over that was the *per one unit*
              convention, and on a two-row card of `3.6412 ₪` nobody is reading it as anything
              else.

              THE PROVENANCE LINE IS NOW ONE FACT: the date. The publisher went this morning; the
              basis goes now; what remains is the only thing on it that a reader cannot get from
              the row above.
            */}
          </p>

          {card.state === 'stale' ? (
            <p className="gp-muted gp-small" data-stale="true">
              {t('market.stale')}
              {card.lastSuccessAtUtc === null ? '' : ` ${asDay(card.lastSuccessAtUtc)}`}
            </p>
          ) : null}
        </>
      )}
    </WidgetPanel>
  );
}
